import type { Installation } from '@si/types';
import { del } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';

interface Params {
  projectId: string;
  installationId: string;
}

export function useDeleteInstallation() {
  const queryClient = useQueryClient();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: ({ projectId, installationId }: Params) =>
      del({
        url: `/projects/${projectId}/installations/${installationId}`,
      }),
    onSuccess: (_, { projectId, installationId }) => {
      const queryKey = queryKeys.getInstallations(projectId);
      const cache = queryClient.getQueryData<{ installations: Installation[] }>(queryKey);

      if (cache) {
        queryClient.setQueryData(queryKey, {
          installations: cache.installations.filter(
            ({ installationId: id }) => id.toString() !== installationId
          ),
        });
      }
    },
  });

  const deleteInstallation = useCallback((params: Params) => mutate(params), [mutate]);

  return {
    deleteInstallation,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
